import { useDrawStore } from 'hooks/stores/useDrawStore';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { ComponentType, ReactElement, useEffect, useState } from 'react';
import { getChooseYourPlanBoostValue } from 'utils/ab-test/abTests';
import { PRIZE_NUMBERS } from 'utils/constants';
import SvgAboutUs from '../public/images/faqs/2025/about-us.svg';
import SvgHowItWorks from '../public/images/faqs/2025/how-it-works.svg';
import SvgPopular from '../public/images/faqs/2025/popular.svg';
import SvgPrizes from '../public/images/faqs/2025/prizes.svg';
import SvgTrees from '../public/images/faqs/2025/trees.svg';
import { teamMemberCount } from './daymadeCompanyInfo';
import { SUGGEST_PRIZE_ID } from './FaqConstants';

interface Faq {
  question: string;
  answer: ReactElement;
}

interface FaqGrouping {
  name: string;
  shortName: string;
  faqs: Faq[];
  iconComponent: ComponentType;
}

const TreeCount = observer(() => {
  const { treesPlantedCount } = useDrawStore();

  return <>{treesPlantedCount?.toLocaleString('en-GB')}</>;
});

const ChooseYourPlanPricingFaq = () => {
  const [pricing, setPricing] = useState({
    premiumPrice: '£2.95',
    litePrice: '£1.50',
  });

  useEffect(() => {
    if (getChooseYourPlanBoostValue() === 'ORIGINALV2') {
      setPricing({
        premiumPrice: '£3.45',
        litePrice: '£2.00',
      });
    }
  }, []);

  return (
    <p>
      A Premium subscription is {pricing.premiumPrice} per week. With a Premium
      subscription, you have access to all the prizes and you have the best
      chances of winning larger prizes. A Lite subscription starts at{' '}
      {pricing.litePrice}. With a Lite subscription you have access to prizes up
      to and including the Diamond category (and the DAYMAKERboost if you
      actively opted in for this). Your chances of winning a (non-Token) prize
      are lower than with a Premium subscription, and you do not play for the
      DAYMAKER jackpot prize. It’s the ideal choice if you want to spend a
      little less every week, while still dreaming of winning that epic holiday!
    </p>
  );
};

const untypedCommonFaqs = {
  howDoesDaymadeWork: {
    question: 'How does DAYMADE work?',
    answer: (
      <p>
        Simple: you enter <Link href="/choose-your-plan">here</Link>, pick 5
        numbers, match to win & choose your prize! We have draws on Tuesday and
        Friday. The more numbers you match, the bigger the prizes you can choose
        from.
      </p>
    ),
  },
  isDaymadeLegit: {
    question: 'Is DAYMADE legit?',
    answer: (
      <>
        <p>
          DAYMADE is a legitimate London-based company that’s been around since
          2019 and has thousands of weekly winners winning prizes big and small.
          Here’s some things you can do yourself to put your mind at ease:
        </p>
        <ul>
          <li>
            Google{' '}
            <a href="https://www.google.com/search?q=DAYMADE" target="_blank">
              DAYMADE
            </a>{' '}
            and check out our{' '}
            <a
              href="https://uk.trustpilot.com/review/daymade.co.uk"
              target="_blank"
            >
              TrustPilot (4.5-stars)
            </a>{' '}
            and{' '}
            <a
              href="https://www.google.com/maps/search/?api=1&query=Google&query_place_id=ChIJKR1Zz3wddkgRqhV07d_OcE0"
              target="_blank"
            >
              Google Reviews (4.8-stars)
            </a>
            .
          </li>
          <li>
            Head to{' '}
            <a href="https://instagram.com/daymadeuk" target="_blank">
              instagram.com/daymadeuk
            </a>{' '}
            where you can see stories from our winners every week.
          </li>
          <li>
            Visit <Link href="/about-us">daymade.co.uk/about-us</Link> where you
            can meet the small, UK based team behind DAYMADE.
          </li>
          <li>
            Search for{' '}
            <a href="https://zeal-ventures.com/" target="_blank">
              Zeal Network
            </a>{' '}
            - the largest Lottery Company in Germany - who are DAYMADE’s
            backers.
          </li>
          <li>
            Head to Companies House and search for{' '}
            <a
              href="https://find-and-update.company-information.service.gov.uk/company/12333770"
              target="_blank"
            >
              DAYMADE Ltd.
            </a>{' '}
            where you can find all the public information about our company.
          </li>
        </ul>
      </>
    ),
  },
  prizes: {
    question: 'What prizes can I win with DAYMADE?',
    answer: (
      <p>
        With a premium DAYMADE entry, you play for all {PRIZE_NUMBERS.PREMIUM}{' '}
        prizes on our website. Think of us like a lottery. With the same entry
        you can win big and small prizes. It all depends on how many numbers you
        match! Our prize offering varies from holidays to experiences to
        lifestyle items and tech gadgets.{' '}
        <Link href="/prizes">Check out our prizes</Link>.
      </p>
    ),
  },
  charities: {
    question: 'What charities do you work with?',
    answer: (
      <p>
        For our tree planting, we work with Veritree. Through them we support
        charities such as Earth Lungs and Mother Tree. We also work with
        Ecologi, a UK-based charity who carbon offsets the flights of our
        holiday winners, as well as our team's carbon footprint.
      </p>
    ),
  },
} as const;
// We can't use as const and satisfies with this version of NextJS/TypeScript, so we emulate it like below
export const commonFaqs: Record<keyof typeof untypedCommonFaqs, Faq> =
  untypedCommonFaqs;

export const AllFAQs: FaqGrouping[] = [
  {
    name: 'Popular questions',
    shortName: 'Popular',
    iconComponent: SvgPopular,
    faqs: [
      commonFaqs.howDoesDaymadeWork,
      commonFaqs.isDaymadeLegit,
      commonFaqs.prizes,
      commonFaqs.charities,
    ],
  },
  {
    name: 'About us',
    shortName: 'About us',
    iconComponent: SvgAboutUs,
    faqs: [
      commonFaqs.howDoesDaymadeWork,
      commonFaqs.isDaymadeLegit,
      {
        question: 'Who runs DAYMADE?',
        answer: (
          <p>
            DAYMADE was founded in the UK by Lisa Nederveen, Callum Wright, and
            Andrew Broekelmann back in 2019. Fast forward and DAYMADE has grown
            into a London-based team of {teamMemberCount} people who are all
            dedicated to leave you with a great experience and winning memories
            to last a lifetime! DAYMADE is backed by Zeal Network, the largest
            lottery company in Germany.
          </p>
        ),
      },
      {
        question: 'How is DAYMADE different from other draws?',
        answer: (
          <p>
            DAYMADE is a new type of prize draw with smaller and more attainable
            non-monetary prizes to create better odds for our players and a more
            fun & fair playing field. Working with small and sustainable prize
            partners across the UK as well as our charity partners tackling
            climate change means there's a win-win model across our entire
            product that we are very proud about.
          </p>
        ),
      },
      {
        question: 'How do you make sure people play responsibly?',
        answer: (
          <p>
            DAYMADE was designed to be enjoyed at your own pace. With a weekly
            subscription capped at four entries per draw and monthly spending
            limits, play stays steady and easy to manage, so it’s fun without
            the worry of overspending. You’re always in control — with the
            option to snooze for up to four weeks, switch to a cheaper plan, or
            cancel anytime from your account. On top of that, our prizes focus
            on travel, experiences, and lifestyle — opportunities to create
            lasting memories rather than chasing cash jackpots. From the prizes
            we offer to game mechanics to how subscriptions work, everything is
            built to keep play fun, responsible, and firmly in your hands.
          </p>
        ),
      },
    ],
  },
  {
    name: 'How it works',
    shortName: 'How it works',
    iconComponent: SvgHowItWorks,
    faqs: [
      commonFaqs.howDoesDaymadeWork,
      {
        question: 'How will I know if I’ve won?',
        answer: (
          <p>
            When you win, we will send you an email. We also send you several
            reminders if you have an unclaimed win. You can always log in to{' '}
            <Link href="/account">your account</Link> to see your wins. Plus,
            you can leave your number in case you want us to give you a call
            when you’ve won big!
          </p>
        ),
      },
      {
        question: 'When I enter the draw, what prizes can I win?',
        answer: (
          <p>
            With a premium DAYMADE entry, you play for all{' '}
            {PRIZE_NUMBERS.PREMIUM} prizes on our website. Think of us like a
            lottery. With the same entry you can win big and small prizes. It
            all depends on how many numbers you match! Our prize offering varies
            from holidays to experiences to lifestyle items and tech gadgets.
            Check out <Link href="/prizes">our prizes</Link> here.
          </p>
        ),
      },
      {
        question: 'How can I enter?',
        answer: (
          <p>
            You can enter by clicking <Link href="/choose-your-plan">here</Link>
            , choosing a plan, picking your 5 lucky numbers and entering your
            details!{' '}
          </p>
        ),
      },
      {
        question: 'What is a DAYMAKERboost?',
        answer: (
          <p>
            The DAYMAKERboost is an optional add-on to your subscription. You
            can add this boost when you check out for the first time, or you can
            do it from your account. The DAYMAKERboost gives you a ‘boost’ on
            the jackpot prize. It means that if you match 5 numbers, your
            jackpot prize will quadruple! This means you’ll get a prize worth
            £100,000, instead of the standard £25,000 jackpot. You can find some
            of the{' '}
            <Link href="/prizes?tier=DAYMAKERboost">
              DAYMAKERboost prizes here
            </Link>
            .
          </p>
        ),
      },
      {
        question: 'Do you offer one-off tickets?',
        answer: (
          <p>
            No, you can only play DAYMADE with a subscription. You can cancel
            your subscription anytime.{' '}
          </p>
        ),
      },
      {
        question: 'Can I easily cancel my subscription?',
        answer: (
          <p>
            Yes, canceling is easy and can be done via{' '}
            <Link href="/account">your account</Link>. If your payment for the
            draw hasn’t been taken yet, you will not be entered into the draw.
            If your payment has already been taken for that week, you will be
            entered into the draw and no further payment will be taken.
          </p>
        ),
      },
      {
        question: 'What is the difference between the plans?',
        answer: <ChooseYourPlanPricingFaq />,
      },
    ],
  },
  {
    name: 'Prizes',
    shortName: 'Prizes',
    iconComponent: SvgPrizes,
    faqs: [
      commonFaqs.prizes,
      {
        question: 'Can I choose my prize?',
        answer: (
          <p>
            Yes, when you win you can choose a prize from the category you’ve
            won in. E.g., when you win a Platinum prize, you can choose from all
            the holidays and lifestyle prizes on offer within this category.
          </p>
        ),
      },
      {
        question: 'If I win a holiday, can I choose the dates?',
        answer: (
          <p>
            Yes, we will always find a suitable option that works for your
            schedule. When you win a holiday, the first thing we’ll ask you is
            your preferred dates and preferred airport. We will then try and
            find a travel option for those dates.
          </p>
        ),
      },
      {
        question: 'Can I bring a guest on my holiday?',
        answer: (
          <p>
            Yes, most of our prizes are for 2 people. But we also offer a few
            solo trips and plenty of family trips. We also can customise some of
            our prizes. For example, if you wish to take a child on one of the
            trips currently advertised as for 2 people, we will try to fit this
            in! We can also customise it more extensively if you’re happy to pay
            out-of-pocket expenses.
          </p>
        ),
      },
      {
        question: 'Do you have prizes for families?',
        answer: (
          <p>
            Yes, many! You can find them{' '}
            <Link href="/prizes/family">right here</Link>.
          </p>
        ),
      },
      {
        question: 'Do you offer cash prizes?',
        answer: (
          <p>
            No, DAYMADE does not offer cash prizes. We focus on prizes that are
            nice to have rather than a necessity! Hence the name ‘day made!’.{' '}
          </p>
        ),
      },
      {
        question: 'Can you customise a trip for people with disabilities?',
        answer: (
          <p>
            Yes, we can customise our holidays to suit people with all kinds of
            disabilities provided they are able to travel with the right
            measures in place. If you have any questions about this, please
            reach out to{' '}
            <a href="mailto:hello@daymade.co.uk">hello@daymade.co.uk</a>.
          </p>
        ),
      },
      {
        question: 'Can I suggest a prize?',
        answer: (
          <p>
            Yes, please! You can do so{' '}
            <Link href={'/prizes#' + SUGGEST_PRIZE_ID}>right here</Link>.
          </p>
        ),
      },
    ],
  },
  {
    name: 'Planting trees',
    shortName: 'Trees',
    iconComponent: SvgTrees,
    faqs: [
      {
        question: 'Where are the trees planted?',
        answer: (
          <p>
            We work with{' '}
            <a href="https://impact.veritree.com/daymade" target="_blank">
              veritree
            </a>{' '}
            who plant verifiable trees donated by DAYMADE players in Kenya,
            Tanzania and Senegal.{' '}
          </p>
        ),
      },
      commonFaqs.charities,
      {
        question: 'How many trees have been donated?',
        answer: (
          <p>
            We are currently at <TreeCount /> trees.
          </p>
        ),
      },
      {
        question: 'How do I know my trees are actually planted?',
        answer: (
          <p>
            Check out our impact page on Veritree that tracks our verifiable
            trees donated:{' '}
            <a
              href="https://www.veritree.com/impact-hub/daymade"
              target="_blank"
            >
              https://www.veritree.com/impact-hub/daymade
            </a>
          </p>
        ),
      },
    ],
  },
];
